/**
 * Month arithmetic and period totals of the Kennzahlen hub (#2175 B1).
 * Port of the pure part of mobile-management/src/features/metrics/format.ts
 * (#1989 D9, D11, D12): the period total by aggregate kind, the honest delta,
 * the delta in points for rates, month shifting. Value formatting is not
 * ported: the web formats through `lib/format.ts` (see `metricFormat.ts`).
 */

import type { MetricAggregate, MetricPoint, MetricUnit } from "./types";

/** The months of `points` inside [from, to] (inclusive, `YYYY-MM`). */
export function pointsInRange(points: MetricPoint[], from: string, to: string): MetricPoint[] {
  return points.filter((p) => p.period >= from && p.period <= to);
}

function finite(v: number | null | undefined): v is number {
  return v !== null && v !== undefined && Number.isFinite(v);
}

export interface PeriodTotals {
  total: number | null;
  previous: number | null;
  /** The month a `last` total stands at (the last month with a value). */
  lastPeriod: string | undefined;
}

/**
 * The period's total from exact monthly values, by the metric's aggregate kind:
 * - `sum`: the months add up. The previous total exists only when every month
 *   with a value has its comparison month (no partial year-ago sums).
 * - `mean`: the mean of the months with a value; a month without a comparison
 *   month is left out of the previous mean.
 * - `last`: the last month with a value and the same month a year earlier.
 */
export function periodTotals(kind: MetricAggregate, points: MetricPoint[]): PeriodTotals {
  const withValue = points.filter((p) => finite(p.value));
  const lastPoint = withValue.at(-1);
  const lastPeriod = lastPoint?.period;
  if (withValue.length === 0) return { total: null, previous: null, lastPeriod };
  const values = withValue.map((p) => p.value as number);
  if (kind === "last") {
    return {
      total: lastPoint?.value ?? null,
      previous: finite(lastPoint?.previous) ? (lastPoint?.previous as number) : null,
      lastPeriod,
    };
  }
  const sum = values.reduce((a, b) => a + b, 0);
  if (kind === "sum") {
    const complete = withValue.every((p) => finite(p.previous));
    const previous = complete ? withValue.reduce((a, p) => a + (p.previous as number), 0) : null;
    return { total: sum, previous, lastPeriod };
  }
  const prev = points.map((p) => p.previous).filter(finite);
  return {
    total: sum / values.length,
    previous: prev.length > 0 ? prev.reduce((a, b) => a + b, 0) / prev.length : null,
    lastPeriod,
  };
}

/** The honest delta in percent: only when a previous total exists and is not 0. */
export function honestDeltaPct(current: number | null, previous: number | null): number | undefined {
  if (current === null || previous === null || previous === 0) return undefined;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** A change against the previous year: in percentage points for rates. */
export type MetricDelta = { kind: "pct"; value: number } | { kind: "pts"; value: number };

export function metricDelta(
  unit: MetricUnit,
  current: number | null,
  previous: number | null,
): MetricDelta | undefined {
  if (current === null || previous === null) return undefined;
  if (unit === "percent") return { kind: "pts", value: current - previous };
  const pct = honestDeltaPct(current, previous);
  return pct === undefined ? undefined : { kind: "pct", value: pct };
}

/** `YYYY-MM` shifted by `months` (negative = earlier). */
export function shiftMonth(month: string, months: number): string {
  const [y, m] = month.split("-").map(Number);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const mon = (index % 12) + 1;
  return `${year}-${String(mon).padStart(2, "0")}`;
}

/** Every `YYYY-MM` from `from` to `to` inclusive (at most 120). */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let cur = from;
  for (let i = 0; i < 120 && cur <= to; i += 1) {
    out.push(cur);
    cur = shiftMonth(cur, 1);
  }
  return out;
}
